// Locale lives in the URL path: "/" and "/en/" are English, "/fr/" is French,
// "/el/" is Greek.
// A real path (rather than a query string or localStorage) means each language
// has its own crawlable URL, and the Back button moves between them naturally.
// Firebase rewrites "**" to index.html, so /fr/ serves the same app shell.

export type Locale = "en" | "fr" | "el" | "nl";

export const LOCALES: Locale[] = ["en", "fr", "el", "nl"];

interface LocaleMeta {
  /** Flag shown in the switcher. */
  flag: string;
  /** Name of the language, written in that language. */
  label: string;
  /** <html lang> value. */
  htmlLang: string;
  /** BCP-47 tag for speechSynthesis (read-aloud fallback voice). */
  speechLang: string;
  /** BCP-47 tag for SpeechRecognition (voice control). */
  recogLang: string;
  /** Matches the voices this language should prefer, e.g. /^fr/i. */
  voicePattern: RegExp;
}

export const LOCALE_META: Record<Locale, LocaleMeta> = {
  en: {
    flag: "🇬🇧",
    label: "English",
    htmlLang: "en",
    speechLang: "en-US",
    recogLang: "en-US",
    voicePattern: /^en/i,
  },
  fr: {
    flag: "🇫🇷",
    label: "Français",
    htmlLang: "fr",
    speechLang: "fr-FR",
    recogLang: "fr-FR",
    voicePattern: /^fr/i,
  },
  el: {
    flag: "🇬🇷",
    label: "Ελληνικά",
    htmlLang: "el",
    speechLang: "el-GR",
    recogLang: "el-GR",
    voicePattern: /^el/i,
  },
  nl: {
    flag: "🇳🇱",
    label: "Nederlands",
    htmlLang: "nl",
    speechLang: "nl-NL",
    recogLang: "nl-NL",
    voicePattern: /^nl/i,
  },
};

/** The locale a pathname selects. Unknown prefixes fall back to English. */
export function localeFromPath(pathname: string): Locale {
  const seg = pathname.split("/")[1]?.toLowerCase();
  return seg === "fr" ? "fr" : seg === "el" ? "el" : seg === "nl" ? "nl" : "en";
}

/** Strip a leading locale segment, returning the rest of the path ("" for root). */
export function stripLocale(pathname: string): string {
  const parts = pathname.split("/");
  const seg = parts[1]?.toLowerCase();
  if (seg === "fr" || seg === "en" || seg === "el" || seg === "nl") parts.splice(1, 1);
  return parts.join("/").replace(/^\/+/, "");
}

/**
 * The URL for `to`, preserving the rest of the path and the query string.
 * English is served from the bare root (no /en/ prefix) so the canonical
 * homepage URL doesn't change; /en/ still resolves for people who type it.
 */
export function urlForLocale(to: Locale, loc: Location = window.location): string {
  const rest = stripLocale(loc.pathname);
  const prefix = to === "en" ? "/" : `/${to}/`;
  return `${prefix}${rest}${loc.search}${loc.hash}`;
}
